import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import { GanttStore } from "@intrasync/stores/features";
import type { GanttTask } from "types/gantt.types";

interface JobSchedule {
  jobScheduleId?: string;
  jobNumber: string;
  phaseName?: string;
  startDate?: string;
  endDate?: string;
  sequence?: number;
  milestones?: Array<{
    name?: string;
    date?: string;
    completed?: boolean;
  }>;
}

interface Panel {
  panelId?: string;
  mark?: string;
  castingDate?: string;
  productType?: string;
  jobNumber?: string;
}

interface ExtendedRootStore extends RootStore {
  jobScheduleStore?: ServiceStore;
  panelStore?: ServiceStore;
}

class JobGanttChartStore {
  // Observables
  isLoading: boolean = false;
  error: string | null = null;
  ganttStore: GanttStore;
  rootStore: ExtendedRootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore as ExtendedRootStore;
    this.ganttStore = new GanttStore(rootStore);
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  // Async actions
  async loadJobScheduleData(jobNumber: string) {
    console.log("📊 Loading job schedule data for job:", jobNumber);

    runInAction(() => {
      this.setLoading(true);
      this.setError(null);
    });

    try {
      // Access the job-schedule service store (should be available as jobScheduleStore)
      const jobScheduleStore = this.rootStore.jobScheduleStore;

      if (!jobScheduleStore) {
        console.error("jobScheduleStore service not found. Available stores:",
          Object.keys(this.rootStore).filter(key => key.includes('job') || key.includes('schedule')));
        throw new Error("jobScheduleStore service is not available");
      }

      const jobScheduleResponse = await jobScheduleStore.find({
        query: {
          $limit: 1000,
        },
      });

      // Load panel data to check casting dates
      const panelStore = this.rootStore.panelStore;

      if (!panelStore) {
        console.error("panelStore service not found");
        throw new Error("panelStore service is not available");
      }

      const panelResponse = await panelStore.find({
        query: {
          jobNumber,
          $limit: 1000
        },
      });

      // Process the data into Gantt tasks - cast the response data to our interfaces
      const tasks = await this.processDataToGanttTasks(
        { data: (jobScheduleResponse?.data as unknown as JobSchedule[]) || [] },
        { data: (panelResponse?.data as unknown as Panel[]) || [] }
      );

      // Update the Gantt store with the tasks
      runInAction(() => {
        this.ganttStore.setTasks(
          tasks as unknown as Parameters<typeof this.ganttStore.setTasks>[0]
        );
        this.setLoading(false);
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load job schedule data";
      console.error("❌ Error loading job schedule data:", error);
      runInAction(() => {
        this.setError(errorMessage);
        this.setLoading(false);
      });
    }
  }

  private async processDataToGanttTasks(
    jobScheduleData: { data?: JobSchedule[] },
    panelData: { data?: Panel[] }
  ): Promise<GanttTask[]> {
    const tasks: GanttTask[] = [];

    // Process job schedule phases
    if (jobScheduleData?.data && Array.isArray(jobScheduleData.data)) {
      jobScheduleData.data.forEach((schedule: JobSchedule, index: number) => {
        // Create main schedule task
        const scheduleTask: GanttTask = {
          id: `schedule-${schedule.jobScheduleId || index}`,
          name: schedule.phaseName || `Phase ${index + 1}`,
          startDate: schedule.startDate || new Date().toISOString(),
          endDate: schedule.endDate || new Date().toISOString(),
          progress: this.calculateScheduleProgress(schedule),
          type: "project",
          dependencies: [],
        };
        tasks.push(scheduleTask);

        // Add subtasks if there are milestone dates
        if (schedule.milestones && Array.isArray(schedule.milestones)) {
          schedule.milestones.forEach((milestone, mIndex: number) => {
            const milestoneTask: GanttTask = {
              id: `milestone-${schedule.jobScheduleId}-${mIndex}`,
              name: milestone.name || `Milestone ${mIndex + 1}`,
              startDate: milestone.date || schedule.startDate,
              endDate: milestone.date || schedule.startDate,
              progress: milestone.completed ? 100 : 0,
              type: "milestone",
              dependencies: [`schedule-${schedule.jobScheduleId || index}`],
            };
            tasks.push(milestoneTask);
          });
        }
      });
    }

    // Process panel production progress
    if (panelData?.data && Array.isArray(panelData.data)) {
      // Group panels by product type
      const panelsByType: { [key: string]: Panel[] } = {};

      panelData.data.forEach((panel: Panel) => {
        const productType = panel.productType || "Unknown";
        if (!panelsByType[productType]) {
          panelsByType[productType] = [];
        }
        panelsByType[productType].push(panel);
      });

      // Create tasks for each product type
      Object.entries(panelsByType).forEach(([productType, panels]) => {
        const totalPanels = panels.length;
        const castedPanels = panels.filter(
          (p: Panel) => p.castingDate && p.castingDate !== "1970-01-01"
        ).length;

        const progress = totalPanels > 0
          ? Math.round((castedPanels / totalPanels) * 100)
          : 0;

        // Find earliest and latest dates for this product type
        const dates = panels
          .filter((p: Panel) => p.castingDate && p.castingDate !== "1970-01-01")
          .map((p: Panel) => new Date(p.castingDate!));

        const startDate = dates.length > 0
          ? new Date(Math.min(...dates.map((d: Date) => d.getTime())))
          : new Date();

        const endDate = dates.length > 0
          ? new Date(Math.max(...dates.map((d: Date) => d.getTime())))
          : new Date();

        const panelTask: GanttTask = {
          id: `panels-${productType}`,
          name: `${productType} Panels (${castedPanels}/${totalPanels})`,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          progress,
          type: "task",
          dependencies: [],
        };
        tasks.push(panelTask);
      });

      // Add overall panel progress task
      const totalPanels = panelData.data.length;
      const totalCasted = panelData.data.filter(
        (p: Panel) => p.castingDate && p.castingDate !== "1970-01-01"
      ).length;

      const overallProgress = totalPanels > 0
        ? Math.round((totalCasted / totalPanels) * 100)
        : 0;

      const allDates = panelData.data
        .filter((p: Panel) => p.castingDate && p.castingDate !== "1970-01-01")
        .map((p: Panel) => new Date(p.castingDate!));

      const overallStartDate = allDates.length > 0
        ? new Date(Math.min(...allDates.map((d: Date) => d.getTime())))
        : new Date();

      const overallEndDate = allDates.length > 0
        ? new Date(Math.max(...allDates.map((d: Date) => d.getTime())))
        : new Date();

      const overallPanelTask: GanttTask = {
        id: "panels-overall",
        name: `Overall Panel Production (${totalCasted}/${totalPanels})`,
        startDate: overallStartDate.toISOString(),
        endDate: overallEndDate.toISOString(),
        progress: overallProgress,
        type: "project",
        dependencies: [],
      };
      tasks.push(overallPanelTask);
    }

    return tasks;
  }

  private calculateScheduleProgress(schedule: JobSchedule): number {
    // Calculate progress based on current date vs schedule dates
    if (!schedule.startDate || !schedule.endDate) {
      return 0;
    }

    const now = new Date();
    const start = new Date(schedule.startDate);
    const end = new Date(schedule.endDate);

    if (now < start) {
      return 0;
    }
    if (now > end) {
      return 100;
    }

    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();

    return Math.round((elapsed / total) * 100);
  }

  // Computed values
  get jobNumber(): string | null {
    return (this.rootStore.routerStore?.params?.jobNumber as string) || null;
  }

  get hasData(): boolean {
    return this.ganttStore.tasks.length > 0;
  }

  get taskCount(): number {
    return this.ganttStore.tasks.length;
  }

  get panelTasks(): GanttTask[] {
    return this.ganttStore.tasks.filter(
      (task) => task.id.startsWith("panels-")
    );
  }

  get scheduleTasks(): GanttTask[] {
    return this.ganttStore.tasks.filter(
      (task) => task.id.startsWith("schedule-")
    );
  }
}

export default JobGanttChartStore;